import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { eyebrow, focusRing, meta } from '../../styles/visualLanguage';
import { isUnifiedReviewDemoEnabled } from './AdvisoryUnifiedReviewDemoPage';
import type { AdvisoryDemoEnvironment } from './AdvisoryCompetitorAnalysisDemoPage';
import type { KeyQuestionDoc } from '../../redux/slices/advisory/advisoryTypes';
import type { SupervisorProposalState } from '../../hooks/advisory/useSupervisorTurn';
import { AdvisoryCalloutState } from './components/AdvisoryCalloutState';
import { AdvisoryKeyQuestionEditor } from './components/AdvisoryKeyQuestionEditor';
import { AdvisoryEngagementWorkspace } from './components/workspace/AdvisoryEngagementWorkspace';
import type {
  AdvisoryEngagementWorkspaceProps,
  AdvisoryWorkspaceCompany,
  AdvisoryWorkspaceSource,
  AdvisoryWorkspaceStage,
  AdvisoryWorkspaceStatus,
} from './components/workspace/advisoryWorkspaceTypes';

const SCENARIOS = ['ordinary', 'competitor', 'acquisition'] as const;
const STATES = [
  'running',
  'slow',
  'needsInput',
  'ready',
  'exploring',
  'answering',
  'partial',
] as const;
type Scenario = (typeof SCENARIOS)[number];
type PreviewState = (typeof STATES)[number];

/** Fixture data stays in the preview route. No service, Firestore write, or supervisor hook. */
function WorkspaceFixture({ scenario, state }: { scenario: Scenario; state: PreviewState }) {
  const { t } = useUntypedTranslation();
  const initiallyStarted = ['exploring', 'answering', 'partial'].includes(state);
  const [started, setStarted] = useState(initiallyStarted);
  const [selectedStage, setSelectedStage] = useState<AdvisoryWorkspaceStage>(
    state === 'exploring'
      ? 'exploration'
      : ['answering', 'partial'].includes(state)
      ? 'answers'
      : 'companies'
  );
  const [editor, setEditor] = useState<AdvisoryEngagementWorkspaceProps['editor']>(null);
  const [notice, setNotice] = useState<string>();
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [questionPending, setQuestionPending] = useState(false);
  const [proposals, setProposals] = useState<SupervisorProposalState | null>(null);
  const companyStatus: AdvisoryWorkspaceStatus =
    initiallyStarted || state === 'ready'
      ? 'ready'
      : state === 'slow'
      ? 'slow'
      : state === 'needsInput'
      ? 'needsInput'
      : 'running';
  const companies: AdvisoryWorkspaceCompany[] =
    scenario === 'ordinary'
      ? []
      : (scenario === 'competitor'
          ? ['Northstar Motors', 'Vertex Automotive']
          : ['Helios Systems']
        ).map((name, index) => ({ id: 'company-' + index, name, status: companyStatus }));
  const [questions, setQuestions] = useState<KeyQuestionDoc[]>(() =>
    [0, 1, 2].map(index => ({
      id: 'fixture-catalog-' + index,
      question: t('advisory:workspace.fixtureQuestion.' + scenario + index),
      order: index,
      subquestions: [t('advisory:workspace.fixtureSubquestion')],
    }))
  );
  const [sources, setSources] = useState<AdvisoryWorkspaceSource[]>(() =>
    companies
      .filter(company => company.status === 'ready')
      .map(company => ({
        id: 'profile-' + company.id,
        companyId: company.id,
        name: company.name,
        kind: 'profile',
        status: 'ready',
      }))
  );
  const gaps = companies
    .filter(company => company.status === 'needsInput')
    .map(company => ({
      sourceId: 'profile-' + company.id,
      generation: 1,
      label: company.name,
      companyId: company.id,
      acknowledged: acknowledged.includes('profile-' + company.id),
    }));
  const pendingCompanies = companies.some(
    company => !['ready', 'completed', 'needsInput'].includes(company.status)
  );
  const blockers = [
    ...(questionPending ? ['advisory:workspace.blocker.questions'] : []),
    ...(pendingCompanies ? ['advisory:workspace.blocker.research'] : []),
    ...(gaps.some(gap => !gap.acknowledged) ? ['advisory:workspace.blocker.gaps'] : []),
    ...(sources.some(source => source.status === 'processing')
      ? ['advisory:workspace.blocker.sources']
      : []),
    ...(editor?.url.trim() ? ['advisory:workspace.blocker.unsavedSource'] : []),
  ];
  const onAddUrl = () => {
    if (!editor || started) return;
    let url: URL;
    try {
      url = new URL(editor.url);
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password)
        throw new Error('invalid');
    } catch {
      setEditor({ ...editor, errorKey: 'advisory:workspace.invalidUrl' });
      return;
    }
    setSources(current => [
      ...current,
      {
        id: 'source-' + current.length,
        companyId: editor.companyId,
        name: url.href,
        url: url.href,
        kind: 'page',
        status: 'processing',
      },
    ]);
    setEditor(null);
    setNotice('advisory:workspace.previewSourceAdded');
  };
  const progressPanel = (answer: boolean) => (
    <Box>
      <Typography component="h2" variant="h6">
        {t('advisory:workspace.stage.' + (answer ? 'answers' : 'exploration'))}
      </Typography>
      <Typography sx={[meta, { mt: 1 }]}>
        {t(
          answer && state === 'partial'
            ? 'advisory:workspace.partialHint'
            : 'advisory:workspace.progressHint'
        )}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
        {questions.map((question, index) => (
          <Box
            component="li"
            key={question.id}
            sx={theme => ({
              display: 'flex',
              justifyContent: 'space-between',
              gap: 3,
              py: 2,
              borderBottom: `1px solid ${theme.visualPrimitives.border}`,
            })}
          >
            <Typography>{question.question}</Typography>
            <Typography sx={[meta]}>
              {t(
                'advisory:workspace.status.' +
                  (answer && state === 'partial'
                    ? index === 1
                      ? 'needsInput'
                      : 'completed'
                    : index === 0
                    ? 'running'
                    : 'waiting')
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
  return (
    <AdvisoryEngagementWorkspace
      goal={t('advisory:workspace.fixtureGoal.' + scenario)}
      selectedStage={selectedStage}
      onSelectStage={setSelectedStage}
      stages={{
        companies: companies.length ? companyStatus : 'ready',
        exploration: started
          ? ['answering', 'partial'].includes(state)
            ? 'completed'
            : 'running'
          : 'waiting',
        answers: state === 'partial' ? 'partial' : state === 'answering' ? 'running' : 'waiting',
      }}
      companies={companies}
      sources={sources}
      gaps={gaps}
      editor={editor}
      locked={started}
      onOpenSources={companyId =>
        setEditor(current => ({
          companyId,
          url: current?.companyId === companyId ? current.url : '',
        }))
      }
      onCloseSources={() => setEditor(null)}
      onUrlChange={url =>
        setEditor(current => (current ? { ...current, url, errorKey: undefined } : null))
      }
      onAddUrl={onAddUrl}
      onAddFiles={(files, companyId) => {
        if (started) return;
        setSources(current => [
          ...current,
          ...files.map((file, index) => ({
            id: 'source-' + (current.length + index),
            name: file.name,
            companyId,
            kind: 'file' as const,
            status: 'processing' as const,
          })),
        ]);
        setNotice('advisory:workspace.previewSourceAdded');
      }}
      onRetrySource={id => {
        if (started) return;
        setSources(current =>
          current.map(source => (source.id === id ? { ...source, status: 'processing' } : source))
        );
      }}
      onAcknowledgeGap={(id, _generation, checked) => {
        if (started) return;
        setAcknowledged(current =>
          checked
            ? [...current.filter(value => value !== id), id]
            : current.filter(value => value !== id)
        );
      }}
      questionPanel={
        <AdvisoryKeyQuestionEditor
          embedded
          onPendingChange={setQuestionPending}
          readOnly={started}
          projectId="preview-only"
          keyQuestions={questions}
          channelState="ready"
          channelErrorCode={null}
          orphaned={{}}
          proposals={proposals}
          onRemove={async question => {
            if (!started)
              setQuestions(current => current.filter(value => value.id !== question.id));
            return null;
          }}
          onAdd={async userQuery => {
            if (!started)
              setProposals({
                userQuery,
                poorMatch: false,
                proposals: [
                  {
                    id: 'fixture-addition',
                    question: t('advisory:workspace.fixtureCatalogQuestion'),
                    component: t('advisory:workspace.fixtureCatalogComponent'),
                    reason: t('advisory:workspace.fixtureCatalogReason'),
                  },
                ],
              });
            return null;
          }}
          onAccept={async id => {
            if (!started && proposals?.proposals.some(proposal => proposal.id === id)) {
              setQuestions(current =>
                current.some(question => question.id === id)
                  ? current
                  : [
                      ...current,
                      {
                        id,
                        question: t('advisory:workspace.fixtureCatalogQuestion'),
                        order: current.length,
                      },
                    ]
              );
              setProposals(null);
              setNotice('advisory:workspace.previewQuestionSaved');
            }
            return null;
          }}
          onBack={() => undefined}
          onConfirm={() => undefined}
          onRetryChannel={() => undefined}
        />
      }
      explorationPanel={started ? progressPanel(false) : undefined}
      answerPanel={['answering', 'partial'].includes(state) ? progressPanel(true) : undefined}
      startBlockers={blockers}
      canStart={!blockers.length && questions.length > 0}
      started={started}
      statusKey={notice}
      onStart={() => {
        setStarted(true);
        setSelectedStage('exploration');
        setEditor(null);
        setNotice('advisory:workspace.previewStarted');
      }}
    />
  );
}

export function AdvisoryWorkspaceDemoPage({
  environment = {
    nodeEnv: process.env.NODE_ENV,
    firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  },
}: {
  environment?: AdvisoryDemoEnvironment;
}) {
  const { t } = useUntypedTranslation();
  const [params, setParams] = useSearchParams();
  const scenario = SCENARIOS.find(value => value === params.get('scenario')) ?? 'acquisition';
  const state = STATES.find(value => value === params.get('state')) ?? 'running';
  if (!isUnifiedReviewDemoEnabled(environment))
    return (
      <AdvisoryCalloutState
        tone="info"
        headingKey="advisory:callout.absent.heading"
        bodyKey="advisory:errors.notFound"
      />
    );
  return (
    <Box
      data-testid="advisory-workspace-preview"
      sx={theme => ({ backgroundColor: theme.palette.background.default })}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2.5, sm: 4.5 }, py: 2 }}>
        <Typography sx={[eyebrow]}>{t('advisory:workspace.previewBadge')}</Typography>
        <Typography sx={[meta]}>{t('advisory:workspace.previewHint')}</Typography>
        {(
          [
            ['scenario', SCENARIOS],
            ['state', STATES],
          ] as const
        ).map(([field, values]) => (
          <Box
            role="group"
            aria-label={t('advisory:workspace.previewGroup.' + field)}
            key={field}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}
          >
            {values.map(value => (
              <Button
                key={value}
                size="small"
                aria-pressed={(field === 'scenario' ? scenario : state) === value}
                onClick={() => setParams({ scenario, state, [field]: value }, { replace: true })}
                sx={[focusRing, { '&[aria-pressed="true"]': { textDecoration: 'underline' } }]}
              >
                {t('advisory:workspace.previewChoice.' + value)}
              </Button>
            ))}
          </Box>
        ))}
      </Box>
      <WorkspaceFixture key={scenario + state} scenario={scenario} state={state} />
    </Box>
  );
}
